import React, { useState, useEffect, forwardRef, useRef, useCallback } from 'react'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { usePatient } from 'hooks/useURL'
import { compose } from 'redux'
import PropTypes from 'prop-types'
import lockable from './lockable.jsx'
import withWorkflow from './withWorkflow.jsx'
import withMessaging from './withMessaging.jsx'
import Window from 'GeneralUI/Window/Window.jsx'
import requiresPrivileges from './requiresPrivileges.jsx'
import checkPatientReadOnly from './checkPatientReadOnly.jsx'
import { cancelPendingAPICallbacks } from 'actions/apiActions'
import withSpinnerFeedback from 'HigherOrder/withSpinnerFeedback.jsx'
import { getContextName } from 'Helper/routeHelpers.js'

const asWindow = () => {
  return WrappedComponent => {
    const ReWrappedComponent = withSpinnerFeedback('window')(
      forwardRef((props, ref) => <WrappedComponent ref={ref} {...props} />)
    )
    const wrappedWindow = forwardRef((props, ref) => {
      const {
        windowConfig,
        windowTitle,
        className = '',
        helpId,
        loading,
        closeHandler,
        zIndexHandler,
        hasPrivileges,
        privilegeErrorMessage,
        isLocked,
        patientIsReadOnly,
        nextStep,
        inWorkflow,
        clearFunction,
        initFunction,
        windowRef // this is a bit hacky but i'm having trouble passing the ref prop through all
        // of the levels necessary to get it here
      } = props
      const { closeable, draggable, resizeable, canFavorite } =
        windowConfig.context
      const nextStepRef = useRef(() => null)
      const [nextAction, setNextAction] = useState(null)
      const [nextEnabled, setNextEnabled] = useState(false)
      const [initRequired, setInitRequired] = useState(false)
      const [windowRequestedTitle, setWindowRequestedTitle] = useState(null)
      const [nextText, setNextText] = useState('Next')

      // if non-null, closeCallback is a function that returns a Promise;
      // that Promise resolves either true (do close) or false (don't close)
      const [closeCallback, setCloseCallback] = useState(null)

      const prescriptionNum = useSelector(
        state => state.patientDisplay?.shared?.prescriptionNum
      )
      const pathname = useSelector(state => state.router?.location?.pathname)

      const dispatch = useDispatch()

      const patientId = usePatient()
      const reduxPatientId = useSelector(
        state => state.patientDisplay?.patientDetails?.patient_id
      )

      let title = windowConfig.name || windowTitle || _.startCase(windowConfig.ref) || ''
      title =
        prescriptionNum != null && windowConfig.context.singleRxWindow
          ? title + ' (' + prescriptionNum + ')'
          : title

      useEffect(() => {
        // componentDidMount
        if (windowConfig.nextButton === 'header' && !windowConfig.modalConfig) {
          setNextAction(prevState => () => nextStepRef.current())
          setNextEnabled(true)
        }
        return () => {
          // component will unmount
          if (clearFunction) {
            clearFunction()
          } else {
            // try dispatching a generic clear based on windowConfig.ref
            const snake = _.snakeCase(windowConfig.ref)
            if (snake !== '') {
              dispatch({ type: 'CLEAR_' + snake.toUpperCase() + '_WINDOW' })
            }
          }
          dispatch(cancelPendingAPICallbacks(windowConfig))
        }
      }, [])

      useEffect(() => {
        nextStepRef.current = nextStep
      }, [nextStep])

      useEffect(() => {
        // run the windows init function when the window first loads and anytime the patient id changes
        if (initFunction && hasPrivileges) {
          // if this is patient context, we cant actually run the init until patient context has completed loading so in that case,
          // we flag that we are ready for an init and watch for those changes in a separate use effect. If this is non patient context, we just call init.
          if (getContextName(pathname) === 'patientDisplay') {
            setInitRequired(true)
          } else {
            initFunction(patientId)
          }
        } else {
          // try dispatching a generic init based on windowConfig.ref
          const snake = _.snakeCase(windowConfig.ref)
          if (snake !== '') {
            dispatch({
              type: 'INIT_' + snake.toUpperCase() + '_WINDOW',
              patientId
            })
          }
        }
      }, [patientId])

      // if the patient id has changed in redux, it should have been updated to match the url. that means the new patients
      // redux patientContext exists so we can begin initing windows that have been waiting
      useEffect(() => {
        if (
          patientId == reduxPatientId &&
          initRequired &&
          initFunction &&
          hasPrivileges
        ) {
          setInitRequired(false)
          initFunction(patientId)
        }
      }, [reduxPatientId, patientId, initRequired])

      const passableSetCloseCallback = useCallback(newCb => setCloseCallback(prevCb => newCb),[])

      return (
        <Window
          ref={windowRef}
          className={className}
          helpId={helpId}
          title={windowRequestedTitle || title}
          isLocked={isLocked || patientIsReadOnly}
          nextText={nextText}
          nextAction={inWorkflow ? nextAction : null}
          windowConfig={windowConfig}
          nextEnabled={nextEnabled}
          closeable={closeable || (!inWorkflow && !windowConfig.asModal)}
          draggable={draggable || (!inWorkflow && !windowConfig.asModal)}
          resizeable={resizeable || (!inWorkflow && !windowConfig.asModal)}
          canFavorite={canFavorite || (!inWorkflow && !windowConfig.asModal)}
          loading={hasPrivileges ? loading : false}
          closeCallback={closeCallback}
          closeHandler={closeHandler}
          zIndexHandler={zIndexHandler}
          alwaysOnTop={windowConfig.modalConfig || false}
          hasPrivileges={hasPrivileges}
          privilegeErrorMessage={privilegeErrorMessage}
        >
          <ReWrappedComponent
            {...props}
            patientId={patientId}
            setCloseCallback={passableSetCloseCallback}
            setWindowTitle={setWindowRequestedTitle}
            nextAction={nextAction}
            setNextAction={newAction => {
              setNextAction(prevAction => newAction)
            }}
            nextText={nextText}
            setNextText={setNextText}
            nextEnabled={nextEnabled}
            setNextEnabled={setNextEnabled}
            inWorkflow={inWorkflow}
          />
        </Window>
      )
    })

    const enhance = compose(
      withWorkflow(),
      requiresPrivileges(),
      lockable(),
      withMessaging(),
      checkPatientReadOnly()
    )
    return enhance(wrappedWindow)
  }
}

export default asWindow

asWindow.propTypes = {
  // WindowConfig passed in by WindowContainer
  windowConfig: PropTypes.object.isRequired,
  // Optional window title to override what is in the window config
  windowTitle: PropTypes.string,
  // PrescriptionNum of the currently selected Rx which patient windows generally
  // need access to for link info between multiple visible windows
  prescriptionNum: PropTypes.string,
  // optional classname to apply to the outer window div
  className: PropTypes.string,
  // Help id to link to in the data/help.js file.
  helpId: PropTypes.string,
  // Should the workflow next button be shown in the enabled state
  nextEnabled: PropTypes.bool,
  // Is the window closeable
  closeable: PropTypes.bool,
  // Is the window draggable
  draggable: PropTypes.bool,
  // Is the window resizable
  resizeable: PropTypes.bool,
  // Should the loading spinner be shown or the window children
  loading: PropTypes.bool,
  // Function passed in by WindowContainer to attach to the window close (X) button
  closeHandler: PropTypes.func,
  // Function passed in by WindowContainer to manage the window z-index value
  zIndexHandler: PropTypes.func,
  // Function passed in to be executed on window load. Typical use would be to make
  // API requests in this function and use loadAfter component so that the loading
  // spinner is shown until results are returned, then children are mounted only
  // after the data is available
  initFunction: PropTypes.func,
  // Counterpart to initFunction this is called when the window unmounts and is a
  // good place to clean info out of the redux store
  clearFunction: PropTypes.func,
  // Function to be executed when user click the nextStep workflow button. By
  // default this will progress to the next step but can be overridden to provide
  // additional functionality (like save window details)
  nextStep: PropTypes.func
}
